export interface TextToSpeechOptions {
  onPlay?: () => void;
  onEnd?: () => void;
  onError?: (error: string) => void;
}

export class TextToSpeechController {
  private audioContext: AudioContext | null = null;
  private playQueue: AudioBuffer[] = [];
  private playingIndex = 0;
  private isPlaying = false;
  private isInitialized = false;
  private isAllChunksReceived = false;
  private options?: TextToSpeechOptions;
  private chunkBuffer: Uint8Array[] = [];
  private bufferFlushTimer: number | null = null;
  private currentSource: AudioBufferSourceNode | null = null;
  private currentSourceGain: GainNode | null = null;
  private readonly MIN_BUFFER_SIZE = 32 * 1024;
  private readonly FLUSH_DELAY_MS = 250;
  private readonly CROSSFADE_DURATION = 0.08;

  cancel() {
    this.reset();
    this.chunkBuffer = [];
    if (this.bufferFlushTimer) {
      clearTimeout(this.bufferFlushTimer);
      this.bufferFlushTimer = null;
    }
  }

  reset() {
    this.isPlaying = false;
    this.isInitialized = false;
    this.isAllChunksReceived = false;
    this.playQueue = [];
    this.playingIndex = 0;
    this.options = undefined;
    this.chunkBuffer = [];
    if (this.bufferFlushTimer) {
      clearTimeout(this.bufferFlushTimer);
      this.bufferFlushTimer = null;
    }
    if (this.currentSource) {
      this.currentSource.stop();
      this.currentSource.disconnect();
      this.currentSource = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.currentSourceGain = null;
  }

  appendChunk(chunk: Uint8Array) {
    this.chunkBuffer.push(chunk);
    this.scheduleBufferFlush();
  }

  private scheduleBufferFlush() {
    const totalLength = this.chunkBuffer.reduce((sum, c) => sum + c.length, 0);
    if (totalLength >= this.MIN_BUFFER_SIZE) {
      this.flushChunkBuffer();
      return;
    }
    if (!this.bufferFlushTimer) {
      this.bufferFlushTimer = window.setTimeout(() => {
        this.flushChunkBuffer();
      }, this.FLUSH_DELAY_MS);
    }
  }

  private flushChunkBuffer(force = false) {
    if (this.bufferFlushTimer) {
      clearTimeout(this.bufferFlushTimer);
      this.bufferFlushTimer = null;
    }
    if (this.chunkBuffer.length === 0) return;
    const totalLength = this.chunkBuffer.reduce((sum, c) => sum + c.length, 0);
    if (!force && totalLength < this.MIN_BUFFER_SIZE) return;
    const combined = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of this.chunkBuffer) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }
    this.chunkBuffer = [];
    this.decodeAndQueueChunk(combined);
  }

  private async decodeAndQueueChunk(chunk: Uint8Array) {
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const buffer = await this.audioContext.decodeAudioData(chunk.buffer.slice(0));
      this.playQueue.push(buffer);
      if (!this.isPlaying) this.playNextBuffer();
    } catch {}
  }

  private playNextBuffer() {
    if (!this.audioContext || this.playingIndex >= this.playQueue.length) {
      if (this.isAllChunksReceived) {
        this.isPlaying = false;
        this.isInitialized = false;
        this.currentSource = null;
        this.currentSourceGain = null;
        this.options?.onEnd?.();
      }
      return;
    }
    const buffer = this.playQueue[this.playingIndex];
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    const gainNode = this.audioContext.createGain();
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    const now = this.audioContext.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(1, now + this.CROSSFADE_DURATION);
    if (this.currentSourceGain) {
      this.currentSourceGain.gain.cancelScheduledValues(now);
      this.currentSourceGain.gain.setValueAtTime(1, now);
      this.currentSourceGain.gain.linearRampToValueAtTime(0, now + this.CROSSFADE_DURATION);
    }
    source.onended = () => {
      this.playingIndex++;
      this.currentSource = null;
      this.currentSourceGain = null;
      if (this.playingIndex < this.playQueue.length) {
        this.playNextBuffer();
      } else if (this.isAllChunksReceived) {
        this.isPlaying = false;
        this.isInitialized = false;
        this.options?.onEnd?.();
      }
    };
    this.currentSource = source;
    this.currentSourceGain = gainNode;
    this.isPlaying = true;
    this.options?.onPlay?.();
    source.start();
  }

  async playFromChunks(options: TextToSpeechOptions = {}) {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.options = options;
    if (this.playQueue.length > 0 && !this.isPlaying) {
      this.playNextBuffer();
    }
  }

  signalAllChunksReceived() {
    if (!this.isPlaying && this.playQueue.length > 0) {
      this.playFromChunks(this.options ?? {});
    }
    this.isAllChunksReceived = true;
    this.flushChunkBuffer(true);
  }
}

export function playStreamingTextToSpeech(options: TextToSpeechOptions = {}) {
  const controller = new TextToSpeechController();
  return {
    controller,
    appendChunk: (chunk: Uint8Array) => controller.appendChunk(chunk),
    playFromChunks: () => controller.playFromChunks(options),
    signalAllChunksReceived: () => controller.signalAllChunksReceived(),
  };
}